import React, { useState } from "react";
import { api } from "../lib/api";
import { Mail, Loader2, CheckCircle2, X } from "lucide-react";

/**
 * Lead contact request form shown only on Pro/Elite attorney cards.
 *
 * Privacy: nothing from the user's prior AI chat is auto-attached.
 * Only what they type + the explicit consent checkbox is sent.
 */
export default function LeadRequestForm({ attorney, county, state, onClose, defaults = {} }) {
    const [form, setForm] = useState({
        name: defaults.name || "",
        email: defaults.email || "",
        phone: defaults.phone || "",
        issue_summary: defaults.issue_summary || "",
        preferred_contact: "email",
        consent: false,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.consent) {
            setErr("Please check the consent box to share your information with this attorney.");
            return;
        }
        if (!form.name.trim() || !form.email.trim() || !form.issue_summary.trim()) {
            setErr("Please fill in your name, email, and a short summary.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/attorneys/${attorney.id}/leads`, {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                state_code: state?.code || null,
                county_id: county?.id || null,
                issue_summary: form.issue_summary,
                preferred_contact: form.preferred_contact,
                consent: true,
            });
            setDone(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't send your request right now. Please try again.");
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <div className="rounded-md bg-primary/5 border border-primary/20 p-4 mt-4" data-testid={`lead-success-${attorney.id}`}>
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Sent
                </div>
                <p className="text-sm text-muted-foreground">
                    Your information was sent to {attorney.name}. They may follow up by your preferred contact method.
                    This is not a guarantee of representation.
                </p>
                {onClose && (
                    <button onClick={onClose} className="text-xs text-primary mt-2 hover:underline" data-testid={`lead-close-${attorney.id}`}>Close</button>
                )}
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="rounded-md border border-border bg-background p-4 mt-4 space-y-3" data-testid={`lead-form-${attorney.id}`}>
            <div className="flex items-center justify-between">
                <div className="font-serif text-sm font-semibold">Want this attorney to contact you?</div>
                {onClose && <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
            </div>
            <p className="text-xs text-muted-foreground">
                Leave your info and a short summary. The attorney may follow up. Optional.
            </p>
            <div className="grid grid-cols-2 gap-2">
                <input className="ll-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid={`lead-name-${attorney.id}`} />
                <input className="ll-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid={`lead-email-${attorney.id}`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <input className="ll-input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={`lead-phone-${attorney.id}`} />
                <select className="ll-input" value={form.preferred_contact} onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })} data-testid={`lead-pref-${attorney.id}`}>
                    <option value="email">Prefer email</option>
                    <option value="phone">Prefer phone</option>
                    <option value="either">Either</option>
                </select>
            </div>
            <textarea
                className="ll-input min-h-[80px]"
                placeholder="Briefly describe your situation (a few sentences is fine)"
                value={form.issue_summary}
                onChange={(e) => setForm({ ...form, issue_summary: e.target.value })}
                maxLength={2000}
                required
                data-testid={`lead-summary-${attorney.id}`}
            />
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid={`lead-consent-label-${attorney.id}`}>
                <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.consent}
                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                    data-testid={`lead-consent-${attorney.id}`}
                />
                <span>I agree to share this information with this attorney so they can contact me about my legal issue.</span>
            </label>
            {err && <div className="text-sm text-destructive" data-testid={`lead-error-${attorney.id}`}>{err}</div>}
            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid={`lead-submit-${attorney.id}`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                <Mail className="h-4 w-4" />
                Send contact request
            </button>
        </form>
    );
}
